import { useMemo } from 'react';

import {
  MultiListSearchDefinition,
  SearchPageSortOption,
  useMultiListSearchPage,
} from 'src/hooks/useSearchPage';
import { flattenFilterOptions } from 'src/hooks/useSearchPage/filterOptions';
import {
  CriteriaFacetStates,
  SearchPageFilterDefinition,
} from 'src/hooks/useSearchPage/sharedTypes';
import { SearchFacetsFetcher } from 'src/hooks/useSearchPage/useSearchFacetsState';
import { useSite } from 'src/hooks/useSite';

import { SearchFilterConfig } from 'src/types/components';

import type { ArticleCardProps } from 'components/BaseHelpers/ArticleCard';

export const newsInsightsSearchFilterNames = ['contenttypes', 'relatedservices'] as const;
export const newsInsightsSearchListKeys = ['newsinsightsblogs', 'events'] as const;

export type NewsInsightsSearchFilterName = (typeof newsInsightsSearchFilterNames)[number];
export type NewsInsightsSearchListKey = (typeof newsInsightsSearchListKeys)[number];
export type NewsInsightsSearchSortValue = 'relevance' | 'date';

export type NewsInsightsSearchResultItem = ArticleCardProps;

type NewsInsightsSearchListDefinition = MultiListSearchDefinition<
  NewsInsightsSearchResultItem,
  NewsInsightsSearchFilterName,
  NewsInsightsSearchSortValue,
  number | null
>;

type NewsInsightsSearchLists = Record<NewsInsightsSearchListKey, NewsInsightsSearchListDefinition>;

type UseNewsInsightsSearchConfig = {
  resultsPerPage: Record<NewsInsightsSearchListKey, number>;
  filters: SearchFilterConfig[];
  /** Build-time criteria-level facet seed (slim shape); absent on facet-outage builds. */
  initialFacetState?: CriteriaFacetStates<NewsInsightsSearchFilterName>;
};

export const newsInsightsSearchSortOptions: SearchPageSortOption<NewsInsightsSearchSortValue>[] = [
  {
    label: 'Relevance',
    value: 'relevance',
  },
  {
    label: 'Date',
    value: 'date',
  },
];

const toFilterDefinitions = (
  filters: SearchFilterConfig[]
): Record<NewsInsightsSearchFilterName, SearchPageFilterDefinition> =>
  filters.reduce<Record<NewsInsightsSearchFilterName, SearchPageFilterDefinition>>(
    (definitions, filter) => {
      if (filter.name !== 'contenttypes' && filter.name !== 'relatedservices') {
        return definitions;
      }

      definitions[filter.name] = {
        label: filter.label,
        options: flattenFilterOptions(filter.options),
      };

      return definitions;
    },
    {
      contenttypes: {
        label: 'Content Type',
        options: [],
      },
      relatedservices: {
        label: 'Services',
        options: [],
      },
    }
  );

export const useNewsInsightsSearch = ({
  resultsPerPage,
  filters,
  initialFacetState,
}: UseNewsInsightsSearchConfig) => {
  const { site } = useSite();
  const filterDefinitions = useMemo(() => toFilterDefinitions(filters), [filters]);

  const facetsFetcher = useMemo<SearchFacetsFetcher<NewsInsightsSearchFilterName>>(
    () =>
      async (criteria, { signal }) => {
        const params = new URLSearchParams();

        params.set('listKey', 'all');
        params.set('facetsOnly', 'true');
        params.set('site', site);

        if (criteria.query) {
          params.set('query', criteria.query);
        }

        Object.entries<string[]>(criteria.filters).forEach(([filterName, values]) => {
          values.forEach((value) => {
            params.append(filterName, value);
          });
        });

        const response = await fetch(`/api/news-insights-search/search?${params.toString()}`, {
          signal,
        });

        if (!response.ok) {
          throw new Error('Unable to load news and insights facets.');
        }

        return response.json();
      },
    [site]
  );

  const lists = useMemo<NewsInsightsSearchLists>(() => {
    const createListDefinition = (
      listKey: NewsInsightsSearchListKey
    ): NewsInsightsSearchListDefinition => ({
      pageSize: resultsPerPage[listKey],
      sortOptions: newsInsightsSearchSortOptions,
      fetcher: async ({ cursor, filters, query, signal, sort, take }) => {
        const params = new URLSearchParams();

        params.set('listKey', listKey);
        params.set('take', String(take));
        params.set('site', site);

        if (query) {
          params.set('query', query);
        }

        if (sort) {
          params.set('sort', sort);
        }

        if (cursor !== undefined && cursor !== null) {
          params.set('cursor', String(cursor));
        }

        Object.entries(filters).forEach(([filterName, values]) => {
          values.forEach((value) => {
            params.append(filterName, value);
          });
        });
        const response = await fetch(`/api/news-insights-search/search?${params.toString()}`, {
          signal,
        });

        if (!response.ok) {
          throw new Error('Unable to load news and insights results.');
        }

        return response.json();
      },
    });

    return {
      newsinsightsblogs: createListDefinition('newsinsightsblogs'),
      events: createListDefinition('events'),
    };
  }, [resultsPerPage, site]);

  return useMultiListSearchPage<NewsInsightsSearchFilterName, NewsInsightsSearchLists>({
    filters: filterDefinitions,
    lists,
    initialFacetState,
    facetsFetcher,
  });
};
